import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { completeMeal, completeMealFailure, completeMealSuccess, deletePlannedMeal, deletePlannedMealFailure, deletePlannedMealSuccess, generateMealSuggestions, generateMealSuggestionsFailure, generateMealSuggestionsSuccess, loadMealPlannerQuota, loadMealPlannerQuotaFailure, loadMealPlannerQuotaSuccess, loadOngoingMeals, loadOngoingMealsFailure, loadOngoingMealsSuccess, loadPlannedMeals, loadPlannedMealsFailure, loadPlannedMealsQuota, loadPlannedMealsQuotaFailure, loadPlannedMealsQuotaSuccess, loadPlannedMealsSuccess, startMeal, startMealFailure, startMealSuccess, upsertPlannedMeal, upsertPlannedMealFailure, upsertPlannedMealSuccess, updatePlannedMealStatus, updatePlannedMealStatusSuccess, updatePlannedMealStatusFailure } from "./meal-planner.actions";
import { catchError, map, of, switchMap } from "rxjs";
import { MealPlannerService } from "../../services/meal-planner.service";
import { MealSuggestionsResponse } from "../../../shared/models/meal-planner.model";
import { NotificationService } from "../../services/notification.service";
import { TranslateService } from "@ngx-translate/core";

export const startMeal$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(MealPlannerService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(startMeal),
      switchMap(({ recipeId, servings }) =>
        service.startMeal({ recipeId, servings }).pipe(
          map(meal => {
            notification.success(translate.instant('mealPlanner.notifications.startSuccess'));
            return startMealSuccess({ meal });
          }),
          catchError(error => {
            notification.error(translate.instant('mealPlanner.notifications.startFailure'));
            return of(startMealFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadMealPlannerQuota$ = createEffect(
  (
    actions$ = inject(Actions),
    mealPlannerService = inject(MealPlannerService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(loadMealPlannerQuota),
      switchMap(() =>
        mealPlannerService.getMealQuota().pipe(
          map((quota) => {
            // Optional success message
            // notification.success('Meal quota loaded.');
            return loadMealPlannerQuotaSuccess({ quota });
          }),
          catchError(() => {
            notification.error(translate.instant('mealPlanner.notifications.quotaFailure'));
            return of(loadMealPlannerQuotaFailure());
          })
        )
      )
    ),
  { functional: true }
);

export const loadOngoingMeals$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(MealPlannerService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(loadOngoingMeals),
      switchMap(() =>
        service.getUserOngoingMeals().pipe(
          map(meals => {
            // Optional success message
            // notification.success('Ongoing meals loaded.');
            return loadOngoingMealsSuccess({ meals });
          }),
          catchError(error => {
            notification.error(translate.instant('mealPlanner.notifications.ongoingFailure'));
            return of(loadOngoingMealsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const completeMeal$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(MealPlannerService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(completeMeal),
      switchMap(({ id }) =>
        service.completeMeal(id).pipe(
          map(() => {
            notification.success(translate.instant('mealPlanner.notifications.completeSuccess'));
            return completeMealSuccess({ id });
          }),
          catchError(error => {
            notification.error(translate.instant('mealPlanner.notifications.completeFailure'));
            return of(completeMealFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const refreshMealPlannerQuotaOnAdd$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(startMealSuccess),
      map(() => loadMealPlannerQuota())
    ),
  { functional: true }
);

export const refreshMealPlannerQuotaOnComplete$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(completeMealSuccess),
      map(() => loadMealPlannerQuota())
    ),
  { functional: true }
);

export const generateMealSuggestions$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(MealPlannerService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(generateMealSuggestions),
      switchMap(({ breakfast, lunch, dinner, snack }) =>
        service.getMealSuggestions({ breakfast, lunch, dinner, snack }).pipe(
          map((response: MealSuggestionsResponse) => {
            if (
              Object.values(response.suggestions).every(
                meals => !meals || meals.recipes.length === 0
              )
            ) {
              notification.error(translate.instant('mealPlanner.notifications.suggestionsFailure'));
              return generateMealSuggestionsFailure({ error: 'No suggestions found', hasStorageItems: response.hasStorageItems });
            }

            notification.success(translate.instant('mealPlanner.notifications.suggestionsSuccess'));
            return generateMealSuggestionsSuccess({
              suggestions: response.suggestions,
              hasStorageItems: response.hasStorageItems
            });
          }),
          catchError(error => {
            notification.error(translate.instant('mealPlanner.notifications.suggestionsFailure'));
            return of(generateMealSuggestionsFailure({ error, hasStorageItems: true }));
          })
        )
      )
    ),
  { functional: true }
);

// Planned Meals Effects
export const loadPlannedMeals$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(MealPlannerService)
  ) =>
    actions$.pipe(
      ofType(loadPlannedMeals),
      switchMap(({ householdId, fromDate, toDate }) =>
        service.getPlannedMeals(householdId, fromDate, toDate).pipe(
          map(plannedMeals => loadPlannedMealsSuccess({ plannedMeals })),
          catchError(error => {
            return of(loadPlannedMealsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const deletePlannedMeal$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(MealPlannerService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(deletePlannedMeal),
      switchMap(({ householdId, plan_date, slot_type, planned_meal_id }) =>
        service.deletePlannedMeal(householdId, plan_date, slot_type, planned_meal_id).pipe(
          map(() => {
            notification.success(translate.instant('mealPlanner.weeklyPlanner.notifications.deleteSuccess'));
            return deletePlannedMealSuccess({ plan_date, slot_type, planned_meal_id });
          }),
          catchError(error => {
            const message = error.status === 400
              ? translate.instant('mealPlanner.weeklyPlanner.notifications.cannotDelete')
              : translate.instant('mealPlanner.weeklyPlanner.notifications.deleteFailure');
            notification.error(message);
            return of(deletePlannedMealFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const upsertPlannedMeal$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(MealPlannerService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(upsertPlannedMeal),
      switchMap(({ householdId, plan_date, slot_type, recipe_id, note }) =>
        service.upsertPlannedMeal(householdId, plan_date, slot_type, recipe_id, note).pipe(
          map(plannedMeal => {
            notification.success(translate.instant('mealPlanner.weeklyPlanner.notifications.addSuccess'));
            return upsertPlannedMealSuccess({ plannedMeal });
          }),
          catchError(error => {
            const message = error.status === 409
              ? translate.instant('mealPlanner.weeklyPlanner.notifications.quotaExceeded')
              : translate.instant('mealPlanner.weeklyPlanner.notifications.addFailure');
            notification.error(message);
            return of(upsertPlannedMealFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadPlannedMealsQuota$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(MealPlannerService)
  ) =>
    actions$.pipe(
      ofType(loadPlannedMealsQuota),
      switchMap(({ householdId, fromDate, toDate }) =>
        service.getPlannedMealsQuota(householdId, fromDate, toDate).pipe(
          map(quota => loadPlannedMealsQuotaSuccess({ quota })),
          catchError(() => of(loadPlannedMealsQuotaFailure()))
        )
      )
    ),
  { functional: true }
);

export const refreshPlannedMealsQuotaOnUpsert$ = createEffect(
  (
    actions$ = inject(Actions)
  ) =>
    actions$.pipe(
      ofType(upsertPlannedMealSuccess),
      map((action) => {
        return loadPlannedMealsQuota({ householdId: action.plannedMeal.household_id });
      })
    ),
  { functional: true }
);

export const refreshPlannedMealsQuotaOnDelete$ = createEffect(
  (
    actions$ = inject(Actions)
  ) =>
    actions$.pipe(
      ofType(deletePlannedMeal),
      map((action) => {
        return loadPlannedMealsQuota({ householdId: action.householdId });
      })
    ),
  { functional: true }
);

export const updatePlannedMealStatus$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(MealPlannerService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(updatePlannedMealStatus),
      switchMap(({ householdId, plannedMealId, status }) =>
        service.updatePlannedMealStatus(householdId, plannedMealId, status).pipe(
          map(plannedMeal => updatePlannedMealStatusSuccess({ plannedMeal: plannedMeal as any })),
          catchError(error => {
            notification.error(translate.instant('mealPlanner.weeklyPlanner.notifications.statusUpdateFailure'));
            return of(updatePlannedMealStatusFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const MealPlannerEffects = {
  startMeal$,  
  loadOngoingMeals$,
  completeMeal$,
  loadMealPlannerQuota$,
  refreshMealPlannerQuotaOnAdd$,
  refreshMealPlannerQuotaOnComplete$,
  generateMealSuggestions$,
  loadPlannedMeals$,
  loadPlannedMealsQuota$,
  deletePlannedMeal$,
  upsertPlannedMeal$,
  refreshPlannedMealsQuotaOnUpsert$,
  refreshPlannedMealsQuotaOnDelete$,
  updatePlannedMealStatus$
};

